import { useCallback, useMemo, useState, type ReactElement } from 'react';
import type { CalendarException, CalendarId, WeekDays } from '@bigint-project/core';
import {
  CalendarEditor,
  CalendarListPanel,
  type CalendarSummary,
} from '../components/calendar/index.js';
import { useProjectStore } from '../store/project-store.js';
import { EmptyState } from './EmptyState.js';

/**
 * 工作日曆維護視圖。
 *
 * 日曆本身（清單、每週工時、例外日、專案預設）全部存在 `project` 裡，改動一律
 * 走 store action → `commit()`，因此會進專案檔、單一 undo 步驟、並觸發排程重排。
 * 這裡唯一的本地 state 是「目前選到哪一份日曆」——那是純 UI 游標，不屬於文件內容，
 * 存進專案檔只會讓兩台機器互相搶著改對方的游標。
 *
 * `components/calendar/**` 是純展示元件（props in / callbacks out），不接 store。
 */
export function CalendarView({ readOnly = false }: { readonly readOnly?: boolean }): ReactElement {
  const calendars = useProjectStore((state) => state.project.calendars);
  const defaultCalendarId = useProjectStore((state) => state.project.defaultCalendarId);
  const addCalendar = useProjectStore((state) => state.addCalendar);
  const deleteCalendar = useProjectStore((state) => state.deleteCalendar);
  const setDefaultCalendar = useProjectStore((state) => state.setDefaultCalendar);
  const updateCalendarWeekDays = useProjectStore((state) => state.updateCalendarWeekDays);
  const updateCalendarExceptions = useProjectStore((state) => state.updateCalendarExceptions);

  const [selectedIdState, setSelectedId] = useState<CalendarId | null>(null);

  const calendarList: readonly CalendarSummary[] = useMemo(
    () => calendars.map((c) => ({ id: c.id, name: c.name, baseCalendarId: c.baseCalendarId })),
    [calendars],
  );
  const allCalendarsMap = useMemo(
    () => new Map(calendars.map((c) => [c.id as string, c])),
    [calendars],
  );

  // 選取的日曆可能已被刪除（或還沒選過任何一份）：一律退回專案預設日曆，
  // 不把「找不到」當成錯誤畫面——那只是游標過期，不是資料有問題。
  const selected =
    calendars.find((c) => c.id === selectedIdState) ??
    calendars.find((c) => c.id === defaultCalendarId) ??
    calendars[0];

  const handleAdd = useCallback(
    (name: string, baseCalendarId: CalendarId | null) => {
      addCalendar(name, baseCalendarId);
      // 新日曆一律附加在尾端（見 commands.addCalendar），直接選取它，
      // 使用者才能立刻開始編輯剛建立的那一份。
      const list = useProjectStore.getState().project.calendars;
      const added = list[list.length - 1];
      if (added !== undefined) setSelectedId(added.id);
    },
    [addCalendar],
  );

  const handleWeekDaysChange = useCallback(
    (weekDays: WeekDays) => {
      if (selected === undefined) return;
      updateCalendarWeekDays(selected.id, weekDays);
    },
    [selected, updateCalendarWeekDays],
  );

  const handleExceptionsChange = useCallback(
    (exceptions: readonly CalendarException[]) => {
      if (selected === undefined) return;
      updateCalendarExceptions(selected.id, exceptions);
    },
    [selected, updateCalendarExceptions],
  );

  if (selected === undefined) {
    return <EmptyState title="尚無日曆" description="請先新增一個工作日曆。" testId="calendar-view-empty" />;
  }

  return (
    <div data-testid="calendar-view" className="grid h-full w-full auto-rows-max gap-4 overflow-y-auto md:auto-rows-auto md:grid-cols-[260px_1fr] md:overflow-hidden">
      <div className="overflow-y-auto rounded border border-gray-200 p-3 dark:border-neutral-700">
        <CalendarListPanel
          readOnly={readOnly}
          calendars={calendarList}
          selectedId={selected.id}
          defaultCalendarId={defaultCalendarId}
          onSelect={setSelectedId}
          onAdd={handleAdd}
          onDelete={deleteCalendar}
          onSetDefault={setDefaultCalendar}
        />
      </div>
      <div className="overflow-y-auto rounded border border-gray-200 p-3 dark:border-neutral-700">
        <CalendarEditor
          readOnly={readOnly}
          calendar={selected}
          allCalendars={allCalendarsMap}
          onWeekDaysChange={handleWeekDaysChange}
          onExceptionsChange={handleExceptionsChange}
        />
      </div>
    </div>
  );
}
